import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import reportCardApi, { errorMessage } from "./reportCardApi";
import ClassMultiSelect from "./ClassMultiSelect";

const EMPTY = { name: "", status: "active", report_sort_order: 0, class_ids: [] };

/** Settings -> Co-Scholastic Subjects (Physical & Health Education, Work Experience ...) */
const CoScholasticSubjectTab = ({ canEdit }) => {
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classMap, setClassMap] = useState({});
  const [edit, setEdit] = useState(null);

  const load = async () => {
    try {
      const list = await reportCardApi.coScholasticSubjects.list();
      setRows(list);
      const map = {};
      await Promise.all(list.map(async (r) => { map[r.id] = await reportCardApi.coScholasticSubjects.getClasses(r.id); }));
      setClassMap(map);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  useEffect(() => {
    load();
    reportCardApi.activeClasses().then(setClasses).catch(() => setClasses([]));
  }, []);

  const className = (id) => classes.find((c) => c.id === id)?.class_name || "";

  const save = async () => {
    if (!edit.name?.trim()) return toast.error("Name is required");
    try {
      const body = {
        name: edit.name.trim(),
        status: edit.status,
        report_sort_order: Number(edit.report_sort_order) || 0,
        class_ids: edit.class_ids,
      };
      if (edit.id) await reportCardApi.coScholasticSubjects.update(edit.id, body);
      else await reportCardApi.coScholasticSubjects.create(body);
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? Subjects that already have grades cannot be deleted; set them Inactive instead.`)) return;
    try {
      await reportCardApi.coScholasticSubjects.remove(row.id);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted mb-0">Graded on the co-scholastic scale (A–E). Leave classes empty to show the subject for every class.</p>
        {canEdit && <Button size="sm" onClick={() => setEdit({ ...EMPTY, report_sort_order: rows.length + 1 })}>New Subject</Button>}
      </div>

      <Table striped bordered size="sm">
        <thead>
          <tr><th style={{ width: 80 }}>Order</th><th>Name</th><th>Classes</th><th style={{ width: 110 }}>Status</th>{canEdit && <th style={{ width: 110 }}>Action</th>}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.report_sort_order}</td>
              <td>{r.name}</td>
              <td><small>{(classMap[r.id] || []).length ? classMap[r.id].map(className).join(", ") : <i className="text-muted">All classes</i>}</small></td>
              <td><span className={`badge bg-${r.status === "active" ? "success" : "secondary"}`}>{r.status}</span></td>
              {canEdit && (
                <td>
                  <Button size="sm" className="edit-button me-2" onClick={() => setEdit({ ...r, class_ids: classMap[r.id] || [] })}><i className="fa-regular fa-pen-to-square"></i></Button>
                  <Button size="sm" variant="danger" onClick={() => remove(r)}><i className="fa fa-trash"></i></Button>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="text-center text-muted">No co-scholastic subjects yet</td></tr>}
        </tbody>
      </Table>

      <Modal show={!!edit} onHide={() => setEdit(null)} centered backdrop="static" size="lg">
        <Modal.Header closeButton><Modal.Title>{edit?.id ? "Edit" : "New"} Co-Scholastic Subject</Modal.Title></Modal.Header>
        <Modal.Body>
          {edit && (
            <Row className="g-3">
              <Col lg={6}>
                <Form.Label className="form-view-label">Name</Form.Label>
                <Form.Control value={edit.name} placeholder="e.g. Work Experience" onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
              </Col>
              <Col lg={3}>
                <Form.Label className="form-view-label">Row Order</Form.Label>
                <Form.Control type="number" min={0} value={edit.report_sort_order} onChange={(e) => setEdit({ ...edit, report_sort_order: e.target.value })} />
              </Col>
              <Col lg={3}>
                <Form.Label className="form-view-label">Status</Form.Label>
                <Form.Select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
              <Col lg={12}>
                <Form.Label className="form-view-label">Classes</Form.Label>
                <ClassMultiSelect classes={classes} value={edit.class_ids} onChange={(ids) => setEdit({ ...edit, class_ids: ids })} />
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEdit(null)}>Close</Button>
          <Button onClick={save}>Save</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CoScholasticSubjectTab;
